import { useLayoutEffect, useSyncExternalStore } from "react";

/**
 * Which ground <html> wears, and the device's theme for the glass one.
 *
 * THE GLASS GROUND (board redesign, 2026-09-10; its light theme, 2026-09-15).
 * The front page, the board, the doors, the documents, the club page, the hub
 * and the admin call `useGlassSurface()`, which puts `ground` on <html> while
 * they are mounted - the pane classes and the `.board-ground` layer in
 * `src/ui/theme.css` key off it - and `dark` beside it unless this device
 * chose the light theme, which flips the tokens in the `.dark` block and
 * fires shadcn's `dark:` classes. On <html> rather than on a wrapper so
 * portals - dialogs, menus, the toast host - inherit both. COUNTED, not
 * toggled: a door can be mounted for a frame beside the page it is about to
 * reveal, and the classes leave only when the last glass surface does. A
 * navigation to the scorekeeper or the bridge must land on the plain light
 * ground those surfaces are designed for.
 *
 * THE THEME is a device choice, like the language (`src/i18n/detect.ts`):
 * `score.theme` in localStorage, and dark when nothing is stored, because the
 * dark glass is the broadcast identity a spectator meets first and the owner
 * chose it for everything a spectator, a captain or an organiser reads. The
 * OS's `prefers-color-scheme` is deliberately not consulted: a phone set to
 * light for its home screen has said nothing about a scoreboard, and the
 * switcher is one tap away on every glass surface (the header, the phone
 * Menu, the doors). `index.html` reads the same key before the bundle loads,
 * so the first paint is the chosen ground and not a flash of the other one.
 */

export type Theme = "dark" | "light";
export const THEMES: readonly Theme[] = ["dark", "light"];
const THEME_KEY = "score.theme";

export function isTheme(value: unknown): value is Theme {
  return value === "dark" || value === "light";
}

/** Safari in private mode throws on `localStorage`. A theme is never worth a
 *  white screen: no store means dark, and a choice still lasts the session. */
function readStored(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return isTheme(stored) ? stored : "dark";
  } catch {
    return "dark";
  }
}

function writeStored(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* no persistence in this browser; the session still has the choice */
  }
}

/** Read on first use, not at import: what is stored is the choice. */
let theme: Theme | null = null;
const listeners = new Set<() => void>();
/** How many glass surfaces are mounted right now. */
let mounted = 0;
/** The `theme-color` the document shipped with, put back when the last glass
 *  surface leaves. */
let shippedThemeColor: string | null = null;

export function getTheme(): Theme {
  if (theme === null) theme = readStored();
  return theme;
}

export function subscribeTheme(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function notify(): void {
  for (const fn of listeners) fn();
}

/**
 * Put the ground on <html> for the glass surfaces that are mounted, or take
 * it off when none is. The browser's own chrome follows: `theme-color` is the
 * ground's colour while a glass surface is up (a light page under a black
 * Safari tab bar reads as a broken page) and goes back to what `index.html`
 * declared when the last one leaves.
 */
function paint(): void {
  const root = document.documentElement;
  const on = mounted > 0;
  root.classList.toggle("ground", on);
  root.classList.toggle("dark", on && getTheme() === "dark");
  // The canvas behind everything, and what iOS shows when the page rubber-
  // bands: the token the ground paints, whichever theme is on. Left in place
  // when the surface leaves, where the light tokens make it the light ground
  // the staff surfaces wear.
  root.style.background = "var(--background)";
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta === null) return;
  if (on) {
    shippedThemeColor ??= meta.content;
    const ground = getComputedStyle(root).getPropertyValue("--background").trim();
    meta.content = ground === "" ? shippedThemeColor : ground;
  } else if (shippedThemeColor !== null) {
    meta.content = shippedThemeColor;
    shippedThemeColor = null;
  }
}

/** Switch, persist, and repaint the ground at once: no React round trip
 *  between the tap and the tokens. */
export function chooseTheme(next: Theme): void {
  if (next === getTheme()) return;
  theme = next;
  writeStored(next);
  paint();
  notify();
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribeTheme, getTheme, getTheme);
}

// A choice made in another tab of the same browser arrives as a storage
// event; this tab follows it rather than showing the other ground until its
// next reload.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== null && event.key !== THEME_KEY) return;
    theme = readStored();
    paint();
    notify();
  });
}

/**
 * A glass surface: the ground on <html> for as long as this component is
 * mounted. A layout effect, so the tokens flip before the first frame on the
 * routes `index.html` cannot know (a gate behind a session, the page nobody
 * registered).
 */
export function useGlassSurface(): void {
  useLayoutEffect(() => {
    mounted += 1;
    paint();
    return () => {
      mounted -= 1;
      paint();
    };
  }, []);
}

/**
 * The scorekeeper's ground: LIGHT, with its control edges hardened for sun
 * (`.sun` in `src/ui/theme.css`). The mirror of `useGlassSurface`, for the
 * same reasons: on <html> so the credit sheet and the setup dialog inherit
 * it, a layout effect so the first frame already has the edges, removed on
 * unmount.
 *
 * It also turns overscroll off for as long as the keeper is open. A downward
 * drag at the top of the page is Android Chrome's pull-to-refresh, and a
 * reload in a dead zone lands a volunteer on the offline gate mid-point; iOS
 * rubber-banding under a fixed band is only noise.
 */
export function useDaylightSurface(): void {
  useLayoutEffect(() => {
    // The ground and the overscroll rule are CSS on the class
    // (`html.sun` in theme.css), so nothing is left behind on unmount.
    const root = document.documentElement;
    root.classList.add("sun");
    return () => root.classList.remove("sun");
  }, []);
}
